import { html, css, LitElement } from 'lit-element';
import { t, tHtml, Localized } from './i18n.js';

// Reference page explaining what each Intrivity role can see and do.
// Available to any authenticated user (no hasRole check) -- reachable at
// /roles once logged in, see intrivity_app.js's pathname check.
// Broadest access first. `can`/`cannot` are how many bullet keys each role has
// (roleGuide.<id>.can1..N).
const ROLES = [
    { id: 'superadmin', accent: 'rgb(250, 166, 53)', manages: true, edits: true, can: 5, cannot: 1 },
    { id: 'admin', accent: 'rgb(82, 162, 170)', manages: true, edits: true, can: 4, cannot: 2 },
    { id: 'clientManager', accent: 'rgb(217, 90, 123)', manages: true, edits: false, can: 2, cannot: 3 },
    { id: 'programManager', accent: 'rgb(175, 208, 117)', manages: true, edits: false, can: 5, cannot: 4 },
    { id: 'trainer', accent: 'rgb(250, 166, 53)', manages: false, edits: false, can: 4, cannot: 3 },
    { id: 'participant', accent: 'rgb(82, 162, 170)', manages: false, edits: false, can: 4, cannot: 2 },
];

export class IntrivityRoleGuide extends Localized(LitElement) {

    static get styles() {
        return css`
            :host {
                display: block;
                font-family: 'Muli', sans-serif;
                color: #222;
                max-width: 820px;
                margin: 0 auto;
                padding: 30px 20px 60px;
            }
            h1 {
                font-size: 1.8em;
                margin: 0 0 6px;
            }
            .lede {
                color: #666;
                max-width: 62ch;
                margin: 0 0 30px;
                line-height: 1.5;
            }
            .glance {
                overflow-x: auto;
                border: 1px solid #e2e2e2;
                border-radius: 8px;
                margin-bottom: 40px;
            }
            table {
                border-collapse: collapse;
                width: 100%;
                min-width: 600px;
                font-size: 0.88em;
            }
            thead th {
                text-align: left;
                font-size: 0.72em;
                letter-spacing: 0.06em;
                text-transform: uppercase;
                color: #6b6b6b;
                padding: 12px 14px;
                border-bottom: 1px solid #e2e2e2;
                background: #f7f7f7;
            }
            tbody td {
                padding: 11px 14px;
                border-bottom: 1px solid #eee;
                vertical-align: top;
                color: #555;
            }
            tbody tr:last-child td { border-bottom: none; }
            tbody td:first-child { color: #222; font-weight: 600; white-space: nowrap; }
            .yes { color: rgb(30, 120, 128); font-weight: 700; }
            .no { color: #6b6b6b; }

            .role {
                border-top: 1px solid #eee;
                padding: 34px 0;
            }
            .role-head {
                display: flex;
                justify-content: space-between;
                align-items: baseline;
                gap: 12px;
                flex-wrap: wrap;
                border-left: 4px solid var(--accent, rgb(250, 166, 53));
                padding-left: 14px;
            }
            .role h2 {
                font-size: 1.3em;
                margin: 0;
            }
            .role-tag {
                font-size: 0.7em;
                letter-spacing: 0.06em;
                text-transform: uppercase;
                color: #6b6b6b;
                white-space: nowrap;
            }
            .dek {
                color: #555;
                max-width: 64ch;
                margin: 14px 0 20px;
                padding-left: 14px;
                line-height: 1.55;
            }
            .split {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 26px;
                padding-left: 14px;
            }
            @media (max-width: 540px) {
                .split { grid-template-columns: 1fr; }
            }
            .col-label {
                font-size: 0.7em;
                letter-spacing: 0.06em;
                text-transform: uppercase;
                font-weight: 700;
                margin-bottom: 8px;
            }
            .col-can .col-label { color: rgb(82, 162, 170); }
            .col-cannot .col-label { color: rgb(217, 90, 123); }
            ul.pillist {
                list-style: none;
                margin: 0;
                padding: 0;
                display: flex;
                flex-direction: column;
                gap: 7px;
            }
            ul.pillist li {
                position: relative;
                padding-left: 16px;
                font-size: 0.93em;
                line-height: 1.4;
            }
            .col-can li::before {
                content: "";
                position: absolute;
                left: 0; top: 0.5em;
                width: 6px; height: 6px;
                border-radius: 50%;
                background: rgb(82, 162, 170);
            }
            .col-cannot li {
                color: #666;
            }
            .col-cannot li::before {
                content: "";
                position: absolute;
                left: 0; top: 0.6em;
                width: 6px; height: 2px;
                background: rgb(217, 90, 123);
            }
            .grant {
                margin: 18px 0 0 14px;
                padding: 10px 14px;
                background: #faf9f6;
                border-left: 3px solid rgb(250, 166, 53);
                border-radius: 0 6px 6px 0;
                font-size: 0.88em;
                color: #555;
            }
            .grant strong { color: #222; }
            code {
                font-family: ui-monospace, "SF Mono", Menlo, monospace;
                font-size: 0.85em;
                background: #fdf1de;
                padding: 0.1em 0.4em;
                border-radius: 4px;
            }
            .stacking {
                margin-top: 30px;
                padding: 18px 20px;
                background: #eef5f5;
                border-radius: 8px;
                font-size: 0.92em;
            }
            .stacking h3 { font-size: 0.95em; margin: 0 0 8px; }
            .stacking p { margin: 0 0 8px; color: #555; line-height: 1.5; }
            .stacking p:last-child { margin-bottom: 0; }
        `;
    }

    render() {
        // Setting names quoted inside the role descriptions come from the same
        // keys the admin screens use, so the two can't drift apart.
        const fields = {
            reportDue: t('field.reportDue'), stopDate: t('field.stopDate'),
            allowTrainers: t('field.allowTrainers'), disablePrivacy: t('field.disablePrivacy'),
            enableAi: t('field.enableAi'),
        };
        const yes = html`<span class="yes">${t('common.yes')}</span>`;
        const no = html`<span class="no">${t('common.no')}</span>`;
        return html`
            <h1>${t('roleGuide.title')}</h1>
            <p class="lede">${t('roleGuide.lede')}</p>

            <div class="glance">
                <table>
                    <thead>
                        <tr>
                            <th>${t('roleGuide.colRole')}</th>
                            <th>${t('roleGuide.colScope')}</th>
                            <th>${t('roleGuide.colManages')}</th>
                            <th>${t('roleGuide.colEdits')}</th>
                            <th>${t('roleGuide.colGranted')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${ROLES.map((r) => html`
                            <tr>
                                <td>${t(`role.${r.id}`)}</td>
                                <td>${t(`roleGuide.${r.id}.scope`)}</td>
                                <td>${r.manages ? yes : no}</td>
                                <td>${r.edits ? yes : no}</td>
                                <td>${t(`roleGuide.${r.id}.grantedShort`)}</td>
                            </tr>
                        `)}
                    </tbody>
                </table>
            </div>

            ${ROLES.map((r) => html`
                <section class="role" style="--accent: ${r.accent};">
                    <div class="role-head">
                        <h2>${t(`role.${r.id}`)}</h2>
                        <span class="role-tag">${t(`roleGuide.${r.id}.tag`)}</span>
                    </div>
                    <p class="dek">${t(`roleGuide.${r.id}.dek`)}</p>
                    <div class="split">
                        <div class="col-can">
                            <div class="col-label">${t('roleGuide.can')}</div>
                            <ul class="pillist">
                                ${Array.from({ length: r.can }, (_, i) => html`<li>${tHtml(`roleGuide.${r.id}.can${i + 1}`, fields)}</li>`)}
                            </ul>
                        </div>
                        <div class="col-cannot">
                            <div class="col-label">${t('roleGuide.cannot')}</div>
                            <ul class="pillist">
                                ${Array.from({ length: r.cannot }, (_, i) => html`<li>${tHtml(`roleGuide.${r.id}.cannot${i + 1}`, fields)}</li>`)}
                            </ul>
                        </div>
                    </div>
                    <div class="grant">${tHtml(`roleGuide.${r.id}.granted`)}</div>
                </section>
            `)}

            <div class="stacking">
                <h3>${t('roleGuide.combineHeading')}</h3>
                <p>${t('roleGuide.combine1')}</p>
                <p>${t('roleGuide.combine2')}</p>
            </div>
        `;
    }
}

if (!customElements.get('intrivity-role-guide')) customElements.define('intrivity-role-guide', IntrivityRoleGuide);
